<template>
  <div id="app">
    <svg width="0" height="0" style="position: absolute; width: 0; height: 0">
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#d4af37" />
        <stop offset="100%" stop-color="#b8860b" />
      </linearGradient>
    </svg>
    <div class="floating-buttons-container">
      <button class="stickyCart" @click="store.isCartSidebarOpen = true">
        <img src="/shopping-cart.svg" alt="cart">
        <span v-if="store.cart.length > 0" class="cart-badge">{{ store.cart.length }}</span>
      </button>

      <a :href="'tel:' + storePhone" class="stickyPhone">
        <img src="/phone.svg" alt="call us">
      </a>
    </div>
    <Header />
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <Footer />

    <CartSidebar v-if="store.isCartSidebarOpen" @close="store.isCartSidebarOpen = false" />
  </div>
</template>

<script setup>
import { computed, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import Header from "./components/HeaderApp.vue";
import Footer from "./components/FooterApp.vue";
import CartSidebar from "./components/CartSidebar.vue";
import { useStoreConfig } from "./services/storeService.js";
import store from "./store.js";
import { initPixels } from "./services/pixelService.js";

const { locale } = useI18n();
const { storeConfig, fetchStoreConfig } = useStoreConfig();

const storePhone = computed(() => storeConfig.value?.contact_phone || storeConfig.value?.phone || "0779134433");

onMounted(async () => {
  const storeData = await fetchStoreConfig();
  if (storeData && storeData.pixels) {
    let pixelsList = storeData.pixels;
    if (typeof storeData.pixels === "string") {
      try {
        pixelsList = JSON.parse(storeData.pixels);
      } catch (e) {
        console.error("Failed to parse pixels:", e);
      }
    }
    initPixels(pixelsList);
  }
});

watch(
  locale,
  (val) => {
    document.documentElement.lang = val;
    document.dir = val === "ar" ? "rtl" : "ltr";
  },
  { immediate: true }
);
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@100;200;300;400;500;600;700&family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap");

:root {
  --primary: #0f172a; /* Deep, elegant dark blue */
  --secondary: #b89254; /* Luxurious gold */
  --accent: #9e7b46; /* Dark goldenrod for active states */
  --danger: #ef4444; /* Standard danger red */
  --dark: #1e293b; /* Sleek slate dark */
  --dark-color: #1e293b; /* Legacy support */
  --light: #fafafa; /* Crisp, clean off-white */
  --gray: #9ca3af; /* Neutral gray */
  --radius: 8px;
  --surface: #f1f5f9; /* Soft input background */
  --border: #e5e7eb; /* Soft border */ /* Modern, softer corners */
}

@keyframes magicalPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(212, 175, 55, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0);
  }
}

.magical-button {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.magical-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(212, 175, 55, 0.2);
}

html body {
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: "IBM Plex Sans Arabic", "Ubuntu", sans-serif;
  background-color: var(--light);
  color: var(--dark);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--dark);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Global Minimalist Button Styles */
button {
  font-family: inherit;
}

/* NProgress Customization */
#nprogress .bar {
  background: var(--secondary) !important;
  height: 4px !important;
}

#nprogress .peg {
  box-shadow: 0 0 10px var(--secondary), 0 0 5px var(--secondary) !important;
}

/* Route Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* Skeleton Loading Animation for Images */
@keyframes skeletonLoading {
  0% {
    background-color: var(--surface);
  }
  50% {
    background-color: #e2e8f0;
  }
  100% {
    background-color: var(--surface);
  }
}

img[loading="lazy"]:not(.btn-icon):not(.action-icon) {
  background: var(--surface);
  animation: skeletonLoading 1.5s infinite;
  color: transparent; /* Hides alt text while loading */
}

/* Once image is loaded, remove animation if we can detect it, but since we rely on native loading="lazy",
   the background will simply be covered by the image once it loads.
   Setting object-fit helps maintain structure. */
img {
  object-fit: cover;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.floating-buttons-container {
  position: fixed;
  bottom: 100px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 15px;
  color: white;
}

.stickyPhone, .stickyCart {
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.4);
  cursor: pointer;
  background-color: var(--secondary);
  color: white;
  transition: all 0.3s ease;
  margin: 0;
  border: none;
  position: relative;
}

.stickyPhone {
  animation: magicalPulse 2s infinite;
  color: white;
}

.stickyPhone:hover, .stickyCart:hover {
  transform: scale(1.1);
  background-color: var(--accent);
}

.stickyPhone svg, .stickyCart svg, .stickyCart img, .stickyPhone img {
  color: white;
  width: 24px;
  height: 24px;
}

.cart-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: var(--danger);
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--light);
}
</style>
